import type { DataSource } from '@unifyapps/defs/types/dataSource';
import type { AggregationMetadataLookupDTOInputs } from '@unifyapps/network/generated/models/aggregationMetadataLookupDTOInputs';
import { useLookupNodeAggregationMetadata } from '@unifyapps/network/generated/workflow-builder-rest-api/workflow-builder-rest-api';
import { keepPreviousData } from '@unifyapps/network/react-query';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { ComputeContextKeys } from '../../../../../hooks/computeContext/const';
import type { QueryRequestResult } from '../../../../../slices/DataSourceControllerSlice/types';
import {
  useDSCActions,
  useDSCDependencyGraphProvider,
  useDSCGetState,
} from '../../../context/selectors';
import { useComputeDataSourcePayload } from '../../../hooks/useComputeDataSourcePayload';

interface AggregationFieldQueryNodeProps {
  dataSourceId: string;
  dataSource?: DataSource;
}

const KEYS_TO_PICK_FROM_INPUTS = ['group', 'entityType', 'object_type', 'projections'];

const useAggregationFieldQueryNode = (props: AggregationFieldQueryNodeProps) => {
  const { dataSourceId, dataSource } = props;

  const getDataSourceControllerState = useDSCGetState();
  const {
    actions: { notifyDependencyChange },
  } = useDSCDependencyGraphProvider();
  const { setAggregationMetadataInDSMeta } = useDSCActions();
  const evaluatedInputs = useComputeDataSourcePayload<{
    value: AggregationMetadataLookupDTOInputs | undefined;
  }>({
    // picking these fields such that the metadata query is not triggered on any payload change, because the metadata is not affected by these fields
    // backend has given the fields that affect the metadata
    data: { value: pick(dataSource?.inputs, KEYS_TO_PICK_FROM_INPUTS) },
    dataSourceId,
    key: 'aggregation',
  });

  const response = useLookupNodeAggregationMetadata(
    {
      context: dataSource?.context,
      inputs: evaluatedInputs.value,
      request: { filterable: true, sortable: true, updatable: true },
    },
    {
      query: {
        enabled: !isEmpty(evaluatedInputs.value),
        retry: false,
        placeholderData: keepPreviousData, // added to avoid empty data on refetch
      },
    },
  );

  const queryResult: QueryRequestResult = useMemo(() => {
    const { isLoading, isFetching, error, data, queryKey } = response;
    return {
      isLoading,
      isFetching,
      error,
      data,
      queryKey,
    };
  }, [response]);

  useEffect(() => {
    const prevDataSourceMetaData = getDataSourceControllerState().dataSourcesMeta[dataSourceId];
    if (isEqual(prevDataSourceMetaData?.aggregationMetadata, queryResult)) return;
    setAggregationMetadataInDSMeta(dataSourceId, queryResult);
    notifyDependencyChange({
      id: ComputeContextKeys.DATA_SOURCES_META,
    });
  }, [
    dataSourceId,
    getDataSourceControllerState,
    notifyDependencyChange,
    queryResult,
    setAggregationMetadataInDSMeta,
  ]);
};

export default useAggregationFieldQueryNode;
